'use client';
// Спільна оболонка для info/legal сторінок (FAQ, оферта, політика, контакти, про нас, повернення).
// Сучасний OLED-premium вигляд на theme-токенах (light/dark). Контент — трилінгвальний (uk/ru/en).
import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';
import { usePriceConvert } from '@/lib/PriceContext';

export type Lang = 'uk' | 'ru' | 'en';
export const useLang = (): Lang => {
  const { lang } = usePriceConvert();
  return (['uk', 'ru', 'en'].includes(lang) ? lang : 'uk') as Lang;
};

// Реквізити продавця/оператора (для оферти, політики, футера).
export const LEGAL_ENTITY = {
  name: 'ОсОО «НОВАПЭЙ»',
  address: 'Кыргызская Республика, г. Бишкек, Свердловский район, ул. Киевская, 62',
  ogrn: '175671-3301-ООО',
  inn: '9909710276',
};

const BACK: Record<Lang, string> = { uk: 'На головну', ru: 'На главную', en: 'Home' };
const UPDATED: Record<Lang, string> = { uk: 'Оновлено', ru: 'Обновлено', en: 'Updated' };

const GROTESK = "'Space Grotesk', sans-serif";

export function LegalShell({ title, subtitle, updated, children }: {
  title: string; subtitle?: string; updated?: string; children: React.ReactNode;
}) {
  const lang = useLang();
  return (
    <div style={{ minHeight: '100dvh', background: 'transparent' }}>
      {/* Hero */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Ambient mint glow */}
        <div aria-hidden style={{ position: 'absolute', top: -110, left: '50%', transform: 'translateX(-50%)', width: 720, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, color-mix(in srgb, var(--gs-green) 10%, transparent), transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ maxWidth: 780, margin: '0 auto', padding: '44px 20px 36px', position: 'relative', zIndex: 1 }}>
          {/* Back pill */}
          <Link href="/" className="ls-back" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--fg-3)', fontSize: 12.5, fontWeight: 600, textDecoration: 'none', padding: '6px 12px 6px 9px', borderRadius: 999, background: 'var(--panel-2)', border: '1px solid var(--line)', marginBottom: 22, transition: 'color .15s, border-color .15s' }}>
            <ChevronLeft size={15} /> {BACK[lang]}
          </Link>
          <h1 style={{ fontFamily: GROTESK, fontSize: 'clamp(30px,4.4vw,36px)', fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.12, margin: 0, color: 'var(--fg)' }}>{title}</h1>
          {subtitle && <p style={{ fontSize: 15, color: 'var(--fg-3)', lineHeight: 1.6, margin: '13px 0 0', maxWidth: 640 }}>{subtitle}</p>}
          {updated && (
            <div style={{ marginTop: 18 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--fg-4)', padding: '5px 11px', borderRadius: 999, background: 'var(--panel-2)', border: '1px solid var(--line)' }}>
                {UPDATED[lang]}: {updated}
              </span>
            </div>
          )}
        </div>
        {/* Bottom hairline */}
        <div aria-hidden style={{ position: 'relative', zIndex: 1, maxWidth: 780, margin: '0 auto', borderTop: '1px solid var(--line)' }} />
      </div>
      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '34px 20px 90px' }}>{children}</div>
      <style>{`.ls-back:hover{color:var(--gs-green)!important;border-color:color-mix(in srgb,var(--gs-green) 34%,transparent)!important}`}</style>
    </div>
  );
}

// Секція з заголовком + контентом. М'яка картка на surface-токені; акцент — мятна крапка (без бокових смуг).
export function LegalSection({ heading, children }: { heading?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 16, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)', padding: '22px 24px' }}>
      {heading && (
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: GROTESK, fontSize: 18, fontWeight: 700, letterSpacing: '-.01em', margin: '0 0 14px', color: 'var(--fg)' }}>
          <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gs-green)', boxShadow: '0 0 8px color-mix(in srgb, var(--gs-green) 55%, transparent)', flexShrink: 0 }} />
          {heading}
        </h2>
      )}
      <div style={{ fontSize: 14.5, color: 'var(--fg-3)', lineHeight: 1.7, maxWidth: 720 }}>{children}</div>
    </section>
  );
}

// Картка реквізитів юр.особи.
export function EntityCard() {
  const lang = useLang();
  const L: Record<Lang, { t: string; company: string; addr: string }> = {
    uk: { t: 'Реквізити оператора', company: 'Компанія', addr: 'Адреса' },
    ru: { t: 'Реквизиты оператора', company: 'Компания', addr: 'Адрес' },
    en: { t: 'Operator details', company: 'Company', addr: 'Address' },
  };
  const row = (k: string, v: string) => (
    <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--line)' }}>
      <div style={{ minWidth: 120, fontSize: 12.5, color: 'var(--fg-3)', flexShrink: 0 }}>{k}</div>
      <div style={{ fontSize: 13.5, color: 'var(--fg)', fontWeight: 600 }}>{v}</div>
    </div>
  );
  return (
    <div style={{ borderRadius: 12, background: 'var(--panel-2)', border: '1px solid var(--line)', padding: '16px 18px', marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--gs-green)', marginBottom: 10 }}>{L[lang].t}</div>
      {row(L[lang].company, LEGAL_ENTITY.name)}
      {row(L[lang].addr, LEGAL_ENTITY.address)}
      {row('ОГРН', LEGAL_ENTITY.ogrn)}
      {row('ИНН', LEGAL_ENTITY.inn)}
    </div>
  );
}
